import {
    Banner,
    ScoreDistribution,
    type ScoreDistributionItem,
    type ScoreDistributionModeOption,
} from "@foodpilot/foods";
import { Box, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DimensionTreeSwitch } from "src/components/ScoreDetail/Dimensions/DimensionTreeSwitch";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";
import { getScoreDetailRemoteState } from "src/context/ScoreDetail/scoreDetailRemoteState";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { ScoreService } from "src/services/ScoreService";
import { type DatavizType, isAllowedChartType } from "src/utils/chart";
import { formatNumber } from "src/utils/formatting";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import {
    getLifecycleItems,
    getImpactItems,
    getNumericalValue,
    getSubscoreItems,
    type ScoreBreakdownItem,
    type ScoreBreakdownMode,
    type StageDefinition,
} from "./productScoreBreakdown";
import { ProductScoreEvolutionCard } from "./ProductScoreEvolutionCard";
import { ProductScoreDistributionSkeleton } from "./ProductScoreDistributionSkeleton";

const adaptScoreBreakdownItems = (items: readonly ScoreBreakdownItem[], language: string): ScoreDistributionItem[] =>
    items.map((item) => {
        const sharedItem = {
            children: adaptScoreBreakdownItems(item.children, language),
            id: item.id,
            ...(item.description ?
                {
                    information: {
                        label: item.label,
                        tooltip: item.description,
                    },
                }
            :   {}),
            label: item.label,
            ...(item.icon ? { icon: item.icon } : {}),
            ...(item.iconTone ? { iconTone: item.iconTone } : {}),
        };

        if (item.value === null) {
            const unavailableItem: ScoreDistributionItem = {
                ...sharedItem,
                state: "unavailable",
                value: null,
            };

            return unavailableItem;
        }

        return {
            ...sharedItem,
            formattedValue: formatNumber(item.value, language, {
                minimumFractionDigits: item.value === 0 ? 0 : 2,
                maximumFractionDigits: 2,
            }),
            unit: item.unit,
            value: item.value,
        };
    });

/**
 * Product adapter for Foods ScoreDistribution.
 *
 * Product owns OLAP traversal, localization, formatting, and loading. Foods
 * owns the complete score-distribution rendering and interaction contract.
 */
export const ProductV2ScoreDistribution = () => {
    const { t, i18n } = useTranslation();
    const {
        score,
        olapScores,
        olapPrevScores,
        isLoading,
        areAggregationsRequested,
        getDimensionValueLocalizedLabel,
        drilldownState,
        prevCampaignLabel,
        scoreWithVariations,
        scoreQueryState,
        scoreQueryError,
        retryScoreQuery,
    } = useScoreDetailContext();
    const [mode, setMode] = useState<ScoreBreakdownMode>("lifecycle");
    const language = i18n.resolvedLanguage ?? i18n.language ?? "en";
    const subscoreDefinitions = score?.children;
    const stages = useMemo<StageDefinition[]>(
        () => [
            {
                id: "ingredients",
                label: t("product.v2.score_live.lca.raw_materials", "Raw Materials"),
                icon: "wheat",
                iconTone: "dataviz-1",
            },
            {
                id: "transformations",
                label: t("product.v2.score_live.lca.processing", "Processing"),
                icon: "transformationSteps",
                iconTone: "dataviz-6",
            },
            {
                id: "packagings",
                label: t("product.v2.score_live.lca.packaging", "Packaging"),
                icon: "packaging",
                iconTone: "dataviz-2",
            },
            {
                id: "transport",
                label: t("product.v2.score_live.lca.transport", "Transport"),
                icon: "transportation",
                iconTone: "dataviz-3",
            },
            {
                id: "distribution",
                label: t("product.v2.score_live.lca.distribution", "Distribution"),
                icon: "distribution",
                iconTone: "dataviz-4",
                useDimensionLabel: true,
            },
            {
                id: "consumption",
                label: t("product.v2.score_live.lca.consumption", "Consumption"),
                icon: "utilisation",
                iconTone: "dataviz-5",
            },
        ],
        [t],
    );
    const supportedSubscoreItems = useMemo(
        () => getSubscoreItems(olapScores, subscoreDefinitions),
        [olapScores, subscoreDefinitions],
    );
    const supportedImpactItems = useMemo(
        () => getImpactItems(olapScores, subscoreDefinitions),
        [olapScores, subscoreDefinitions],
    );
    const hasSupportedSubscores = supportedSubscoreItems.length > 0;
    const hasSupportedImpacts = supportedImpactItems.length > 0;
    const resolvedMode: ScoreBreakdownMode =
        mode === "subscores" && hasSupportedSubscores ? "subscores"
        : mode === "impacts" && hasSupportedImpacts ? "impacts"
        : "lifecycle";
    const breakdownItems = useMemo(() => {
        if (olapScores === undefined) return [];
        if (resolvedMode === "subscores") return supportedSubscoreItems;
        if (resolvedMode === "impacts") return supportedImpactItems;
        return getLifecycleItems(olapScores, stages, getDimensionValueLocalizedLabel);
    }, [
        getDimensionValueLocalizedLabel,
        olapScores,
        resolvedMode,
        stages,
        supportedImpactItems,
        supportedSubscoreItems,
    ]);
    const items = useMemo(() => adaptScoreBreakdownItems(breakdownItems, language), [breakdownItems, language]);
    const hasExpandableItems = items.some((item) => item.children.length > 0);
    const modes: ScoreDistributionModeOption[] = [
        {
            id: "lifecycle",
            label: t("product.score_details.lifecycle_stages", "Lifecycle stages"),
        },
        ...(hasSupportedSubscores ?
            [
                {
                    id: "subscores" as const,
                    label: t("product.score_details.subscores", "Sub-scores"),
                },
            ]
        :   []),
        ...(hasSupportedImpacts ?
            [
                {
                    id: "impacts" as const,
                    label: t("product.score_details.impacts", "Impacts"),
                },
            ]
        :   []),
    ];
    const specificity = OlapScoresService.getSubscoreWithType("specificity-rate", olapScores);
    const specificityValue = getNumericalValue(specificity);
    const resolvedScoreQueryState =
        scoreQueryState ??
        getScoreDetailRemoteState({
            score: olapScores,
            isLoading: isLoading ?? false,
            error: scoreQueryError,
        });

    if (resolvedScoreQueryState.status === "loading" || areAggregationsRequested) {
        return <ProductScoreDistributionSkeleton label={t("common.loading", "Loading...")} />;
    }

    if (resolvedScoreQueryState.status === "empty") {
        return (
            <Stack role="status">
                <Banner variant="info">{t("product.score_details.no_data", "No score details available")}</Banner>
            </Stack>
        );
    }

    if (resolvedScoreQueryState.status === "success-unavailable") {
        return (
            <Stack role="status">
                <Banner variant="info">{t("product.score_details.score_unavailable", "Score unavailable")}</Banner>
            </Stack>
        );
    }

    if (
        resolvedScoreQueryState.status === "contract-error" ||
        resolvedScoreQueryState.status === "forbidden" ||
        resolvedScoreQueryState.status === "not-found" ||
        resolvedScoreQueryState.status === "server-error"
    ) {
        return <ScoreQueryError context="details" onRetry={retryScoreQuery} status={resolvedScoreQueryState.status} />;
    }

    const currentDimension = OlapScoresService.getDimension(drilldownState?.last?.type, olapScores?.dimensions);
    const currentDimensionValue = OlapScoresService.getDimensionValue(drilldownState?.last?.id, currentDimension);
    const previousDimension = OlapScoresService.getDimension(drilldownState?.last?.type, olapPrevScores?.dimensions);
    const previousDimensionValue = OlapScoresService.getDimensionValue(drilldownState?.last?.id, previousDimension);
    const currentComparisonValue = OlapScoresService.normalizeNumericalScore(
        drilldownState?.last ? currentDimensionValue?.score.value : olapScores?.score.value,
    );
    const previousComparisonValue = OlapScoresService.normalizeNumericalScore(
        drilldownState?.last ? previousDimensionValue?.score.value : olapPrevScores?.score.value,
    );
    const scoreWithVariation =
        score?.id === undefined ?
            undefined
        :   ScoreService.getScoreWithVariationById(score.id, scoreWithVariations ?? []);
    const datavizType = scoreWithVariation?.datavizType as DatavizType | undefined;
    const chartType = datavizType && isAllowedChartType(datavizType) ? datavizType : undefined;
    const displaysPreviousCampaign =
        chartType !== "raw" &&
        typeof currentComparisonValue === "number" &&
        typeof previousComparisonValue === "number";

    const distribution = (
        <Box
            sx={(theme) => {
                const desktopBreakdownWidth =
                    resolvedMode === "subscores" ? theme.custom.scoreDetail.subscoreBreakdownWidth
                    : resolvedMode === "impacts" ? theme.custom.scoreDetail.impactBreakdownWidth
                    : theme.custom.scoreDetail.lifecycleBreakdownWidth;

                return {
                    ...(resolvedMode === "lifecycle" && !hasExpandableItems ?
                        {
                            "& [data-testid='score-distribution-breakdown'] > .MuiStack-root > .MuiStack-root:first-of-type > .MuiStack-root:first-of-type > [aria-hidden='true']:first-of-type":
                                {
                                    display: "none",
                                },
                        }
                    :   {}),
                    "& [data-testid='score-distribution-breakdown'] .MuiStack-root:has(> [role='progressbar'])": {
                        width: "100%",
                        [theme.breakpoints.up("md")]: {
                            width: desktopBreakdownWidth,
                        },
                    },
                    "& [data-testid='score-distribution-breakdown'] .MuiStack-root:has(> .MuiStack-root > [role='progressbar'])":
                        {
                            width: "100%",
                            [theme.breakpoints.up("md")]: {
                                width: "auto",
                            },
                        },
                    "& [data-testid='score-distribution-breakdown'] [role='progressbar']": {
                        flexGrow: 1,
                        [theme.breakpoints.up("md")]: {
                            flexGrow: 0,
                        },
                    },
                };
            }}
        >
            <ScoreDistribution
                displayByLabel={t("company.overview.variations.display_by", "Display by:")}
                emptyLabel={t("product.score_details.no_data", "No score details available")}
                getCollapseLabel={(item) =>
                    t("product.score_details.collapse", "Collapse {{label}}", { label: item.label })
                }
                getExpandLabel={(item) => t("product.score_details.expand", "Expand {{label}}", { label: item.label })}
                items={items}
                modes={modes}
                onModeChange={(nextMode) => {
                    if (
                        nextMode === "lifecycle" ||
                        (nextMode === "subscores" && hasSupportedSubscores) ||
                        (nextMode === "impacts" && hasSupportedImpacts)
                    ) {
                        setMode(nextMode);
                    }
                }}
                selectedMode={resolvedMode}
                showSummary={false}
                summary={{
                    emptyLabel: t("product.score_details.score_unavailable", "Score unavailable"),
                    unit: olapScores?.unit ?? "",
                    value: getNumericalValue(olapScores),
                    ...(specificityValue !== null ?
                        {
                            specificityLabel: t(
                                "product.score_details.data_specificity_rate",
                                "Data specificity rate: {{value}} %",
                                { value: specificityValue },
                            ),
                        }
                    :   {}),
                }}
            />
        </Box>
    );

    const improvement =
        scoreWithVariation?.improvement ?? (scoreWithVariation?.displayMode === "carbon" ? "decrease" : "increase");

    return (
        <Stack
            alignItems="flex-start"
            data-testid="product-v2-score-distribution"
            direction={{ xs: "column", lg: "row" }}
            sx={(theme) => ({ gap: theme.spacing(3) })}
        >
            <Box
                sx={(theme) => ({
                    flexShrink: 0,
                    maxWidth: theme.custom.scoreDetail.summaryWidth,
                    width: "100%",
                    [theme.breakpoints.down("sm")]: {
                        maxWidth: "100%",
                    },
                })}
            >
                <ProductScoreEvolutionCard
                    currentValue={currentComparisonValue}
                    improvement={improvement}
                    precision={scoreWithVariation?.precision ?? 2}
                    previousCampaignLabel={prevCampaignLabel}
                    previousValue={displaysPreviousCampaign ? previousComparisonValue : null}
                    unit={olapScores?.unit ?? ""}
                />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>{distribution}</Box>
        </Stack>
    );
};

export const ProductScoreDistribution = () => {
    const isProductUxV2 = useIsProductUxV2();

    return (
        <DimensionTreeSwitch
            appearance={isProductUxV2 ? "product-v2" : "legacy"}
            mode="distribution"
            distributionContent={isProductUxV2 ? <ProductV2ScoreDistribution /> : undefined}
        />
    );
};
